import {
  amountScale,
  currencyCodeSchema,
  isZeroMoney,
  parseAmountInput,
  parseRateInput,
} from '@familywallet/shared';
import { z } from 'zod';

interface OpeningInput {
  currency: string;
  openingBalance: string;
  openingRate: string;
}

/** The three form fields of an opening balance, typed as people type them. */
export const openingFields = {
  currency: currencyCodeSchema,
  openingBalance: z.string(),
  openingRate: z.string(),
};

/**
 * Checks an opening balance in any currency: the amount as typed ("500k", "1.5m", "1,500,000")
 * must be valid for its currency (LBP: whole numbers), and a one-off rate is required only when
 * the currency has no family rate yet. `latestRates` is today's family rate (1 base = X).
 * Use in superRefine, then `openingOutput` in transform.
 */
export function checkOpening(
  v: OpeningInput,
  ctx: z.RefinementCtx,
  baseCurrency: string,
  latestRates: Readonly<Record<string, string>>,
): void {
  const amount = parseOpening(v.openingBalance, v.currency);
  if (amount === null) {
    ctx.addIssue({
      code: 'custom',
      path: ['openingBalance'],
      message: amountScale(v.currency) === 0 ? 'whole' : 'amount',
    });
    return;
  }
  if (needsOneOffRate(v.currency, amount, baseCurrency, latestRates)) {
    if (!parseRateInput(v.openingRate)) {
      ctx.addIssue({ code: 'custom', path: ['openingRate'], message: 'rate' });
    }
  }
}

/** The request fields for a checked opening balance. */
export function openingOutput(
  v: OpeningInput,
  baseCurrency: string,
  latestRates: Readonly<Record<string, string>>,
): { currency: string; openingBalance?: string; openingRate?: string } {
  const amount = parseOpening(v.openingBalance, v.currency) ?? undefined;
  const rate = needsOneOffRate(v.currency, amount, baseCurrency, latestRates)
    ? (parseRateInput(v.openingRate) ?? undefined)
    : undefined;
  return { currency: v.currency, openingBalance: amount, openingRate: rate };
}

/** undefined when empty, null when not a valid amount for the currency. */
export function parseOpening(raw: string, currency: string): string | undefined | null {
  return raw.trim() === '' ? undefined : parseAmountInput(raw, currency);
}

export function needsOneOffRate(
  currency: string,
  amount: string | undefined | null,
  baseCurrency: string,
  latestRates: Readonly<Record<string, string>>,
): boolean {
  return (
    currency !== baseCurrency &&
    !!amount &&
    !isZeroMoney(amount) &&
    latestRates[currency] === undefined
  );
}
